import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { 
  BookOpen, 
  RefreshCw,
  Search,
  Loader2,
  TrendingUp,
  TrendingDown,
  Wallet,
  FileText,
  Download,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientLedgerPage = () => {
  const { getAuthHeader } = useAuth();
  
  // State
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [savingOB, setSavingOB] = useState(false);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Fetch clients
  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/clients?limit=500`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const clientList = data.data || data;
        // Sort clients alphabetically by name
        const sortedClients = clientList.sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );
        setClients(sortedClients);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setClientsLoading(false);
    }
  }, [getAuthHeader]);

  // Fetch ledger data
  const fetchLedger = useCallback(async () => {
    if (!selectedClientId) {
      setLedgerData(null);
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      
      const response = await fetch(
        `${API_URL}/api/clients/${selectedClientId}/ledger?${params}`,
        { headers: getAuthHeader() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLedgerData(data);
        setOpeningBalance(data.client?.opening_balance || '');
      } else {
        toast.error('Failed to fetch ledger');
      }
    } catch (err) {
      toast.error('Failed to fetch ledger');
    } finally {
      setLoading(false);
    }
  }, [selectedClientId, fromDate, toDate, getAuthHeader]);

  // Initial fetch
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Fetch ledger when client changes
  useEffect(() => {
    if (selectedClientId) {
      fetchLedger();
    }
  }, [selectedClientId, fetchLedger]);

  // Filter clients by search term
  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.client_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const saveOpeningBalance = async () => {
    if (!selectedClientId) return;
    setSavingOB(true);
    try {
      const response = await fetch(
        `${API_URL}/api/clients/${selectedClientId}/opening-balance`,
        {
          method: 'PATCH',
          headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ opening_balance: parseFloat(openingBalance) || 0 })
        }
      );
      if (response.ok) {
        toast.success('Opening balance updated');
        fetchLedger();
      } else {
        toast.error('Failed to update opening balance');
      }
    } catch {
      toast.error('Failed to update opening balance');
    } finally {
      setSavingOB(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              Client Ledger
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              View client-wise sales and payment transactions
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Client Selection */}
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Select Client</Label>
                <SearchableSelect
                  options={clients.map(c => ({ 
                    value: c.id, 
                    label: c.client_code ? `${c.name} (${c.client_code})` : c.name 
                  })).sort((a, b) => a.label.localeCompare(b.label))}
                  value={selectedClientId}
                  onValueChange={setSelectedClientId}
                  placeholder={clientsLoading ? "Loading clients..." : "Select a client..."}
                  searchPlaceholder="Search clients..."
                  emptyText="No clients found"
                  data-testid="client-select"
                  className="mt-1"
                />
              </div>
              
              {/* Date Range */}
              <div>
                <Label className="text-sm font-medium">From Date</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">To Date</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                  <Button 
                    variant="outline" 
                    onClick={fetchLedger}
                    disabled={!selectedClientId || loading}
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {ledgerData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Invoiced</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(ledgerData.summary?.total_invoiced)}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Payments</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(ledgerData.summary?.total_payments)}
                    </p>
                  </div>
                  <TrendingDown className="w-8 h-8 text-green-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Outstanding</p>
                    <p className={`text-2xl font-bold ${ledgerData.summary?.closing_balance > 0 ? 'text-orange-600' : 'text-gray-600'}`}>
                      {formatCurrency(ledgerData.summary?.closing_balance)}
                    </p>
                  </div>
                  <Wallet className="w-8 h-8 text-orange-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Transactions</p>
                    <p className="text-2xl font-bold text-gray-700">
                      {ledgerData.summary?.transaction_count || 0}
                    </p>
                  </div>
                  <FileText className="w-8 h-8 text-gray-200" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Client Info + Opening Balance */}
        {ledgerData?.client && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{ledgerData.client.name}</h2>
                  <div className="flex gap-4 text-sm text-gray-500 mt-1">
                    {ledgerData.client.client_code && (
                      <span>Code: {ledgerData.client.client_code}</span>
                    )}
                    {ledgerData.client.gst_number && (
                      <span>GST: {ledgerData.client.gst_number}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Opening Balance</Label>
                  <Input
                    type="number"
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                    className="w-40 text-right"
                    placeholder="0"
                    data-testid="client-opening-balance"
                  />
                  <Button
                    size="sm"
                    onClick={saveOpeningBalance}
                    disabled={savingOB}
                    data-testid="save-client-ob"
                  >
                    {savingOB ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ledger Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Transaction Details</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Document</TableHead>
                  <TableHead>Particulars</TableHead>
                  <TableHead className="text-right">Debit (Sales)</TableHead>
                  <TableHead className="text-right">Credit (Payment)</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!selectedClientId ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Select a client to view ledger</p>
                    </TableCell>
                  </TableRow>
                ) : loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                      <p className="text-gray-500 mt-2">Loading ledger...</p>
                    </TableCell>
                  </TableRow>
                ) : !ledgerData?.ledger?.length ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-gray-500">
                      <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No transactions found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {ledgerData.ledger.map((entry, idx) => (
                      <TableRow 
                        key={idx}
                        className={entry.type === 'payment' ? 'bg-green-50' : entry.type === 'opening_balance' ? 'bg-amber-50 font-semibold' : ''}
                      >
                        <TableCell className="font-medium">
                          {formatDate(entry.date)}
                        </TableCell>
                        <TableCell>
                          <span className="font-mono text-sm">{entry.document}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {entry.type === 'invoice' ? (
                              <Badge variant="outline" className="text-blue-600 border-blue-200">
                                Invoice
                              </Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">
                                Payment
                              </Badge>
                            )}
                            <span className="text-sm text-gray-600">{entry.particulars}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {entry.debit ? formatCurrency(entry.debit) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {entry.credit ? formatCurrency(entry.credit) : '-'}
                        </TableCell>
                        <TableCell className="text-right font-bold">
                          {formatCurrency(entry.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow className="bg-gray-100 font-bold">
                      <TableCell colSpan={3} className="text-right">
                        Total
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(ledgerData.summary?.total_invoiced)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(ledgerData.summary?.total_payments)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(ledgerData.summary?.closing_balance)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ClientLedgerPage;
